// Languages the LangCraft pronunciation API supports, using its language codes
// and names. Keep in sync with backend/services/langcraft_service.py.
export const LEARNING_LANGUAGES = [
  { code: "ar", name: "Arabic", flag: "🇸🇦" },
  { code: "bn", name: "Bengali", flag: "🇧🇩" },
  { code: "ca", name: "Catalan", flag: "🇪🇸" },
  { code: "hr", name: "Croatian", flag: "🇭🇷" },
  { code: "cs", name: "Czech", flag: "🇨🇿" },
  { code: "da", name: "Danish", flag: "🇩🇰" },
  { code: "nl", name: "Dutch", flag: "🇳🇱" },
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "fi", name: "Finnish", flag: "🇫🇮" },
  { code: "fr", name: "French", flag: "🇫🇷" },
  { code: "de", name: "German", flag: "🇩🇪" },
  { code: "el", name: "Greek", flag: "🇬🇷" },
  { code: "he", name: "Hebrew", flag: "🇮🇱" },
  { code: "hi", name: "Hindi", flag: "🇮🇳" },
  { code: "hu", name: "Hungarian", flag: "🇭🇺" },
  { code: "it", name: "Italian", flag: "🇮🇹" },
  { code: "ja", name: "Japanese", flag: "🇯🇵" },
  { code: "ko", name: "Korean", flag: "🇰🇷" },
  { code: "lt", name: "Lithuanian", flag: "🇱🇹" },
  { code: "zh", name: "Mandarin Chinese", flag: "🇨🇳" },
  { code: "no", name: "Norwegian", flag: "🇳🇴" },
  { code: "fa", name: "Persian", flag: "🇮🇷" },
  { code: "pl", name: "Polish", flag: "🇵🇱" },
  { code: "pt", name: "Portuguese", flag: "🇵🇹" },
  { code: "ro", name: "Romanian", flag: "🇷🇴" },
  { code: "ru", name: "Russian", flag: "🇷🇺" },
  { code: "sk", name: "Slovak", flag: "🇸🇰" },
  { code: "es", name: "Spanish", flag: "🇪🇸" },
  { code: "sw", name: "Swahili", flag: "🇹🇿" },
  { code: "sv", name: "Swedish", flag: "🇸🇪" },
  { code: "ta", name: "Tamil", flag: "🇮🇳" },
  { code: "te", name: "Telugu", flag: "🇮🇳" },
  { code: "th", name: "Thai", flag: "🇹🇭" },
  { code: "tr", name: "Turkish", flag: "🇹🇷" },
  { code: "uk", name: "Ukrainian", flag: "🇺🇦" },
  { code: "vi", name: "Vietnamese", flag: "🇻🇳" },
];

export const DEFAULT_LEARNING_LANGUAGE = "en";

// Regional accents LangCraft scores against, keyed by language. Script
// variants (Shavian, Pinglish, romanised Mandarin) are left out.
// Keep in sync with DIALECTS in backend/services/langcraft_service.py.
export const LANGUAGE_DIALECTS = {
  ca: [
    { code: "ca-ba", name: "Balearic", flag: "🇪🇸" },
    { code: "ca-nw", name: "North-western", flag: "🇪🇸" },
    { code: "ca-va", name: "Valencian", flag: "🇪🇸" },
  ],
  en: [
    { code: "en-us", name: "American", flag: "🇺🇸" },
    { code: "en-us-nyc", name: "New York City", flag: "🇺🇸" },
    { code: "en-gb", name: "British", flag: "🇬🇧" },
    { code: "en-gb-x-rp", name: "Received Pronunciation", flag: "🇬🇧" },
    { code: "en-gb-scotland", name: "Scottish", flag: "🏴󠁧󠁢󠁳󠁣󠁴󠁿" },
    { code: "en-gb-x-gbclan", name: "Lancaster", flag: "🇬🇧" },
    { code: "en-gb-x-gbcwmd", name: "West Midlands", flag: "🇬🇧" },
    { code: "en-au", name: "Australian", flag: "🇦🇺" },
    { code: "en-029", name: "Caribbean", flag: "🇯🇲" },
  ],
  es: [{ code: "es-419", name: "Latin American", flag: "🇲🇽" }],
  fr: [
    { code: "fr-fr", name: "France", flag: "🇫🇷" },
    { code: "fr-be", name: "Belgium", flag: "🇧🇪" },
    { code: "fr-ch", name: "Switzerland", flag: "🇨🇭" },
    { code: "fr-ca", name: "Québec", flag: "🇨🇦" },
  ],
  no: [{ code: "nb", name: "Bokmål", flag: "🇳🇴" }],
  pt: [{ code: "pt-br", name: "Brazil", flag: "🇧🇷" }],
  ru: [
    { code: "ru-cl", name: "Classic", flag: "🇷🇺" },
    { code: "ru-lv", name: "Latvia", flag: "🇱🇻" },
  ],
  vi: [
    { code: "vi-vn-x-central", name: "Central", flag: "🇻🇳" },
    { code: "vi-vn-x-south", name: "Southern", flag: "🇻🇳" },
  ],
};

// Flat picker list: each language ("Standard"), then its accents indented.
export const LEARNING_LANGUAGE_OPTIONS = LEARNING_LANGUAGES.flatMap((language) => [
  { code: language.code, label: language.name, flag: language.flag, isDialect: false },
  ...(LANGUAGE_DIALECTS[language.code] || []).map((dialect) => ({
    code: dialect.code,
    label: `${language.name} · ${dialect.name}`,
    flag: dialect.flag,
    isDialect: true,
  })),
]);

const SUPPORTED_CODES = new Set(LEARNING_LANGUAGES.map(({ code }) => code));
const DIALECT_CODES = new Set(
  Object.values(LANGUAGE_DIALECTS).flatMap((dialects) => dialects.map(({ code }) => code))
);
const LANGUAGE_ALIASES = { nb: "no", nn: "no", iw: "he", cmn: "zh" };

// Base LangCraft language for any stored code ("en-gb" -> "en"), or null.
export const baseLearningLanguage = (language) => {
  if (typeof language !== "string" || !language) {
    return null;
  }
  const base = language.trim().toLowerCase().split(/[-_]/)[0];
  const code = LANGUAGE_ALIASES[base] || base;
  return SUPPORTED_CODES.has(code) ? code : null;
};

// Map any stored language code (e.g. "en-us", "pt_BR") to a picker value:
// the dialect when LangCraft supports it, otherwise the base language.
export const toLearningLanguage = (language) => {
  const base = baseLearningLanguage(language);
  if (!base) {
    return null;
  }
  const full = language.trim().toLowerCase().replace(/_/g, "-");
  return DIALECT_CODES.has(full) ? full : base;
};

// Two-letter country code for a language's flag (for the flag-icons CSS classes).
export const countryForLanguage = (language) => {
  const code = toLearningLanguage(language);
  if (code === "en-gb-scotland") {
    return "gb-sct";
  }
  const entry = LEARNING_LANGUAGE_OPTIONS.find((option) => option.code === code);
  const letters = [...(entry?.flag || "")]
    .map((char) => char.codePointAt(0) - 127397)
    .filter((point) => point >= 65 && point <= 90);
  if (letters.length !== 2) {
    return null;
  }
  return String.fromCharCode(...letters).toLowerCase();
};
